import { memo } from "react"

import {
	canPublishSupportPortal,
	SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS,
} from "@productlane/lib/src/plans-client"
import { Loader, Select, Switch } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import { PortalSettingsCard } from "./portal-settings-card"
import { PublishPortalCard } from "./publish-portal-card"
import { SettingItem } from "./settings-switches/setting-item"
import { SettingsContainer } from "./settings-switches/settings-container"

export const SupportPortalSettingsContent = memo(
	({ className }: { className?: string }) => {
		const zero = useZero()
		const workspace = useZeroWorkspace()
		const planQuery = api.workspace.activePlan.useQuery(undefined, {
			cacheTime: Infinity,
			staleTime: Infinity,
		})

		if (!workspace) {
			return null
		}

		const { widgetSettings } = workspace
		const isPublished = workspace.customerPortalSettings?.isEnabled || false
		const canPublish = canPublishSupportPortal({
			plan: planQuery.data,
			isCurrentlyPublished: isPublished,
		})

		return (
			<div className={className}>
				<PublishPortalCard
					action={
						canPublish ? undefined : (
							<UpgradeButton
								required={SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS}
								size="sm"
								className="flex-none rounded-full"
							/>
						)
					}
					checked={isPublished}
					onCheckedChange={async (value) => {
						await mutateOk(
							zero.mutate(
								mutators.workspaces.update({
									id: workspace.id,
									customerPortalSettings: {
										isEnabled: value,
										teamRequests:
											workspace.customerPortalSettings?.teamRequests || false,
									},
								}),
							),
						)
						toast.success({
							title: `Support Portal ${!workspace.customerPortalSettings?.isEnabled ? "is now public" : "is now hidden"}`,
						})
						analytics.track("toggle-customer-portal-enabled", {
							value,
							enabled: true,
						})
					}}
					title="Support Portal"
					description="Customers can access the support portal with this link."
					link="support"
				/>

				<PortalSettingsCard dimmed={canPublish && !isPublished} />

				<SettingsContainer>
					<TeamRequestsSwitch />

					<PortalCompanyScopeSelect className="pt-4" />

					{widgetSettings?.isEnabled && (
						<SettingItem
							className="pt-4"
							title="Show in widget"
							description="Add a link to your Support Portal in the widget, if Live Chat is not enabled."
							action={
								canPublish ? (
									<Switch
										name="show-in-widget"
										label={null}
										checked={widgetSettings.isSupportEnabled}
										onCheckedChange={async (value) => {
											if (widgetSettings.isEnabled) {
												await mutateOk(
													zero.mutate(
														mutators.widgetSettings.update({
															id: widgetSettings.id,
															workspaceId: workspace.id,
															isSupportEnabled: value,
														}),
													),
												)
											}
											analytics.track("toggle-support-portal-show-in-widget", {
												value,
												popover: true,
											})
										}}
									/>
								) : (
									<UpgradeButton
										required={SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS}
										size="sm"
										className="flex-none rounded-full"
									/>
								)
							}
						/>
					)}

					<SettingItem
						className="pt-4"
						title="Due dates"
						description="Mirror the due date for Linear issues and projects."
						action={
							<Switch
								name="due-date"
								label={null}
								checked={workspace.isDueDateEnabled}
								onCheckedChange={async (value) => {
									await mutateOk(
										zero.mutate(
											mutators.workspaces.update({
												id: workspace.id,
												isDueDateEnabled: value,
											}),
										),
									)
									analytics.track("toggle-support-portal-due-date", {
										value,
										popover: true,
									})
								}}
							/>
						}
					/>
				</SettingsContainer>
			</div>
		)
	},
)

type PortalCompanyScope = "STRICT" | "ALLOW_UNTAGGED" | "OFF"

const PORTAL_COMPANY_SCOPE_DOCS_ARTICLE_ID =
	"7e861235-26a4-4b00-9268-a0e82258bbe6"

const PORTAL_COMPANY_SCOPE_OPTIONS: {
	value: PortalCompanyScope
	label: string
}[] = [
	{ value: "STRICT", label: "Strict" },
	{ value: "ALLOW_UNTAGGED", label: "Lenient" },
	{ value: "OFF", label: "Off" },
]

const PortalCompanyScopeSelect = ({ className }: { className?: string }) => {
	const zero = useZero()
	const workspace = useZeroWorkspace()

	if (!workspace) {
		return null
	}

	const value: PortalCompanyScope =
		workspace.customerPortalSettings?.portalCompanyScope ?? "OFF"

	const setScope = async (next: PortalCompanyScope) => {
		if (next === value) return
		await mutateOk(
			zero.mutate(
				mutators.workspaces.update({
					id: workspace.id,
					customerPortalSettings: {
						portalCompanyScope: next,
						teamRequests:
							workspace.customerPortalSettings?.teamRequests ?? false,
						isEnabled: workspace.customerPortalSettings?.isEnabled ?? false,
					},
				}),
			),
		)
		analytics.track("update-portal-company-scope", { value: next })
	}

	return (
		<SettingItem
			className={className}
			title="Cross-company thread visibility"
			description={
				<>
					Whether contacts can see threads from their previous companies.{" "}
					<button
						type="button"
						onClick={() => {
							// @ts-expect-error Global widget calls
							// eslint-disable-next-line
							window.Productlane.openDocs(PORTAL_COMPANY_SCOPE_DOCS_ARTICLE_ID)
						}}
						className="text-primary underline underline-offset-2 transition-opacity duration-150 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] hover:opacity-80"
					>
						Learn more
					</button>
				</>
			}
			action={
				<Select
					name="portal-company-scope"
					value={value}
					onChange={(next) => setScope(next as PortalCompanyScope)}
					options={PORTAL_COMPANY_SCOPE_OPTIONS}
				/>
			}
		/>
	)
}

export const TeamRequestsSwitch = ({ className }: { className?: string }) => {
	const zero = useZero()
	const workspace = useZeroWorkspace()

	if (!workspace) {
		return (
			<div className="flex items-center justify-center">
				<Loader />
			</div>
		)
	}

	return (
		<SettingItem
			className={className}
			title="Team requests"
			description="Customers see every request from their company. Customers without a company see only their own."
			action={
				<Switch
					name="team"
					checked={workspace.customerPortalSettings?.teamRequests}
					onCheckedChange={async (value) => {
						await mutateOk(
							zero.mutate(
								mutators.workspaces.update({
									id: workspace.id,
									customerPortalSettings: {
										teamRequests: value,
										showInWidget:
											workspace.customerPortalSettings?.showInWidget,
										isEnabled: workspace.customerPortalSettings?.isEnabled,
									},
								}),
							),
						)

						analytics.track("toggle-team-requests", {
							value,
							enabled: true,
						})
					}}
				/>
			}
		/>
	)
}
